import React from 'react';
import {
  Box,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  FormHelperText,
  Grid,
  TextField,
  Typography,
} from '@mui/material';
import InterveneSearchField from './InterveneSearchField';
import FileInput from '../../../components/Shared/FileUploadPreview/FileInput';
import {
  getButtonsWithExamples,
  getHeaderAcceptValue,
} from '../utils/whatsappTemplateUtils';
import useInterveneSx from '../hooks/useInterveneSx';
import useInterveneTheme from '../hooks/useInterveneTheme';
import { SELECT_MODE } from '../../../components/ui/SearchableSelect/constants';

function SectionTitle({ children }) {
  return (
    <Typography
      variant="subtitle2"
      sx={{
        mb: 1,
        mt: 2,
        fontWeight: 600,
        color: 'text.secondary',
      }}
    >
      {children}
    </Typography>
  );
}

export default function InterveneSendTemplateForm({
  phoneNumber,
  templateList = [],
  selectedTemplate,
  isLoadingTemplates,
  isLoadingMoreTemplates = false,
  bodyParams,
  buttonUrlParams,
  headerComponent,
  headerFile,
  useCreateImage,
  errors,
  isWhatsappTemplate,
  searchTemplates,
  loadMoreTemplates,
  selectTemplate,
  updateBodyParam,
  updateButtonParam,
  selectHeaderFile,
  removeHeaderFile,
  setHeaderFile,
  toggleUseCreateImage,
}) {
  const styles = useInterveneSx().dialog;
  const { chat } = useInterveneTheme();
  const buttonsWithExamples = getButtonsWithExamples(selectedTemplate);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 0.5,
      }}
    >
      <TextField
        fullWidth
        label="Phone Number"
        size="small"
        value={phoneNumber}
        disabled
        sx={styles.field}
        helperText="Sending to the active chat user"
      />

      {isLoadingTemplates && !templateList.length ? (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            py: 3,
          }}
        >
          <CircularProgress
            size={28}
            sx={{ color: chat.primary }}
          />
        </Box>
      ) : (
        <InterveneSearchField
          compact
          spaced
          mode={SELECT_MODE.INFINITE}
          options={templateList}
          value={selectedTemplate}
          label="Select Template"
          labelKey="name"
          valueKey="md_id"
          onChange={selectTemplate}
          onSearchInputChange={searchTemplates}
          onLoadMore={loadMoreTemplates}
          loading={isLoadingTemplates}
          loadingMore={isLoadingMoreTemplates}
        />
      )}

      {selectedTemplate && !isWhatsappTemplate && (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mt: 2 }}
        >
          Only WhatsApp templates can be sent from Intervene.
        </Typography>
      )}

      {selectedTemplate && isWhatsappTemplate && (
        <>
          {bodyParams.length > 0 && (
            <Box>
              <SectionTitle>Body Parameters</SectionTitle>

              <Grid container spacing={1}>
                {bodyParams.map((item, index) => (
                  <Grid item xs={12} sm={6} key={`body-${index}`}>
                    <TextField
                      fullWidth
                      size="small"
                      label={item?.label || 'Body Parameter'}
                      value={item?.text || ''}
                      onChange={(e) =>
                        updateBodyParam(index, e.target.value)
                      }
                      error={Boolean(
                        errors[`bodyParams_${index}`]
                      )}
                      helperText={
                        errors[`bodyParams_${index}`] || ''
                      }
                      sx={styles.field}
                    />
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}

          {headerComponent &&
            headerComponent.format !== 'TEXT' && (
              <Box>
                <SectionTitle>
                  Header {headerComponent.format?.toLowerCase()}
                </SectionTitle>

                {!useCreateImage && (
                  <>
                    <FileInput
                      onFileSelect={selectHeaderFile}
                      onFileRemove={removeHeaderFile}
                      file={headerFile}
                      setFile={setHeaderFile}
                      accept={getHeaderAcceptValue(
                        headerComponent.format
                      )}
                      chooseButtonSx={styles.chooseFileButton}
                    />

                    {errors.headerFile && (
                      <FormHelperText
                        error
                        sx={{ mt: 0.5 }}
                      >
                        {errors.headerFile}
                      </FormHelperText>
                    )}

                    <FormHelperText sx={{ mt: 1 }}>
                      {headerComponent.format === 'IMAGE' &&
                        'Allowed: JPG, PNG • Max 5 MB'}

                      {headerComponent.format === 'VIDEO' &&
                        'Allowed: MP4, 3GP • Max 16 MB'}

                      {headerComponent.format === 'DOCUMENT' &&
                        'Allowed: PDF, DOC, DOCX • Max 95 MB'}
                    </FormHelperText>
                  </>
                )}

                {selectedTemplate?.media?.header ? (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={useCreateImage}
                        onChange={(e) =>
                          toggleUseCreateImage(
                            e.target.checked
                          )
                        }
                        size="small"
                      />
                    }
                    label={
                      <Typography
                        variant="body2"
                        component="span"
                      >
                        Use the template&apos;s pre-created{' '}
                        {headerComponent.format?.toLowerCase()}
                      </Typography>
                    }
                    sx={{ mt: 1 }}
                  />
                ) : null}
              </Box>
            )}

          {buttonsWithExamples.length > 0 && (
            <Box>
              <SectionTitle>Buttons</SectionTitle>

              {buttonsWithExamples.map(
                (button, buttonIndex) => {
                  const paramIndex =
                    button.originalIndex ?? buttonIndex;

                  return (
                    <Box
                      key={`button-${paramIndex}`}
                      sx={{ mb: 2 }}
                    >
                      <Typography
                        variant="body2"
                        sx={{ mb: 1 }}
                      >
                        {buttonIndex + 1}. {button?.text}
                      </Typography>

                      <TextField
                        fullWidth
                        size="small"
                        label={`Enter ${buttonIndex + 1}`}
                        value={
                          buttonUrlParams[paramIndex] || ''
                        }
                        onChange={(e) =>
                          updateButtonParam(
                            paramIndex,
                            e.target.value
                          )
                        }
                        error={Boolean(
                          errors[
                            `buttonUrlParams_${paramIndex}`
                          ]
                        )}
                        helperText={
                          errors[
                            `buttonUrlParams_${paramIndex}`
                          ] || ''
                        }
                        sx={styles.field}
                      />
                    </Box>
                  );
                }
              )}
            </Box>
          )}
        </>
      )}
    </Box>
  );
}
